// src/components/features/pivot/PivotResults.jsx
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { FixedSizeList as List } from 'react-window';
import LoadingSpinner from '../../common/LoadingSpinner';
import ErrorMessage from '../../common/ErrorMessage';
import { useNavigate } from 'react-router-dom';

const PivotContainer = styled.div`
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const PivotHeader = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
`;

const PivotTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
`;

const PivotSubtitle = styled.p`
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0;
`;

const PivotValue = styled.code`
  font-family: 'Monaco', 'Courier New', monospace;
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  color: #0066cc;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;

  h4 {
    margin: 0 0 0.5rem 0;
    font-size: 0.875rem;
    color: #6b7280;
    text-transform: uppercase;
  }

  p {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #0066cc;
  }
`;

const RelatedCountContainer = styled.div`
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const RelatedCountTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1e40af;
  margin: 0 0 1rem 0;
`;

const RelatedCountList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const CountItem = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 4px;
  border-left: 3px solid #0066cc;

  strong {
    display: block;
    font-size: 0.9rem;
    color: #1f2937;
    margin-bottom: 0.25rem;
  }

  span {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0066cc;
  }
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 2rem;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  background-color: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  font-weight: 600;
  color: #1f2937;
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const AppIcon = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: contain;
`;

const HashLink = styled.button`
  background: none;
  border: none;
  color: #0066cc;
  cursor: pointer;
  text-decoration: none;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const MalwareBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: ${props => props.type === 'unknown' ? '#f3f4f6' : '#fee2e2'};
  color: ${props => props.type === 'unknown' ? '#4b5563' : '#991b1b'};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: ${props => {
    switch (props.status) {
      case 'completed':
        return '#dcfce7';
      case 'analyzing':
        return '#dbeafe';
      case 'failed':
        return '#fee2e2';
      default:
        return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed':
        return '#166534';
      case 'analyzing':
        return '#1e40af';
      case 'failed':
        return '#991b1b';
      default:
        return '#4b5563';
    }
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #9ca3af;

  h3 {
    margin-top: 1rem;
    color: #6b7280;
  }

  p {
    margin: 0.5rem 0 0 0;
    font-size: 0.9rem;
  }
`;

const VirtualTableWrapper = styled.div`
  margin-top: 2rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 50px 120px 150px 150px 150px 120px 150px;
  background-color: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const HeaderCell = styled.div`
  padding: 1rem;
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
`;

const VirtualRow = styled.div`
  display: grid;
  grid-template-columns: 50px 120px 150px 150px 150px 120px 150px;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  
  &:hover {
    background-color: #f9fafb;
  }
`;

const RowCell = styled.div`
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.875rem;
`;

// Row component for virtualized list
const Row = ({ index, style, data }) => {
  const result = data[index];
  const navigate = useNavigate();

  return (
    <div style={style}>
      <VirtualRow>
        <RowCell>
          {result.icon ? (
            <AppIcon
              src={`data:image/png;base64,${result.icon}`}
              alt="Icon"
              onError={(e) => {
                e.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
              }}
            />
          ) : (
            <AppIcon
              src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
              alt="No Icon"
            />
          )}
        </RowCell>
        <RowCell>
          <HashLink onClick={() => navigate(`/analysis/${result.sha256}`)} title={result.sha256}>
            {result.sha256.substring(0, 8)}...
          </HashLink>
        </RowCell>
        <RowCell title={result.package_name}>{result.package_name}</RowCell>
        <RowCell title={result.app_name}>{result.app_name}</RowCell>
        <RowCell>
          <MalwareBadge type={result.malware_family === 'Unknown' ? 'unknown' : 'malware'}>
            {result.malware_family || 'Unknown'}
          </MalwareBadge>
        </RowCell>
        <RowCell>
          <StatusBadge status={result.analysis_status}>{result.analysis_status}</StatusBadge>
        </RowCell>
        <RowCell>
          {result.timestamp ? new Date(result.timestamp).toLocaleDateString() : 'N/A'}
        </RowCell>
      </VirtualRow>
    </div>
  );
};

const PivotResults = ({ pivotData, loading, error, onResultClick }) => {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('timestamp');

  if (loading) {
    return <LoadingSpinner>Loading pivot results...</LoadingSpinner>;
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  if (!pivotData) {
    return null;
  }

  const results = pivotData.results || [];
  const relatedCount = pivotData.related_count || {};
  const isCorrelated = pivotData.pivot_type === 'correlated_samples';

  const renderRelatedCount = () => {
    if (!relatedCount || Object.keys(relatedCount).length === 0) {
      return null;
    }

    return (
      <RelatedCountContainer>
        <RelatedCountTitle>Related Information</RelatedCountTitle>
        <RelatedCountList>
          {relatedCount.by_malware_family && (
            <>
              {Object.entries(relatedCount.by_malware_family).map(([family, count]) => (
                <CountItem key={family}>
                  <strong>{family}</strong>
                  <span>{count} APK{count !== 1 ? 's' : ''}</span>
                </CountItem>
              ))}
            </>
          )}
          {relatedCount.by_package_name && (
            <>
              {Object.entries(relatedCount.by_package_name).slice(0, 3).map(([pkg, count]) => (
                <CountItem key={pkg}>
                  <strong>{pkg}</strong>
                  <span>{count} APK{count !== 1 ? 's' : ''}</span>
                </CountItem>
              ))}
            </>
          )}
        </RelatedCountList>
      </RelatedCountContainer>
    );
  };

  return (
    <PivotContainer>
      <PivotHeader>
        <PivotTitle>
          {pivotData.pivot_type === 'icon_hash' && 'Icon Hash Pivot Results'}
          {pivotData.pivot_type === 'certificate_sha256' && 'Certificate Pivot Results'}
          {pivotData.pivot_type === 'permission' && 'Permission Pivot Results'}
          {pivotData.pivot_type === 'malware_family' && 'Malware Family Pivot Results'}
          {pivotData.pivot_type === 'related_artifacts' && 'Related Artifacts'}
          {pivotData.pivot_type === 'artifact_hash' && 'Samples containing this artifact'}
          {pivotData.pivot_type === 'correlated_samples' && 'Correlated Samples'}
          {pivotData.pivot_type === 'service' && 'Service Pivot'}
          {pivotData.pivot_type === 'receiver' && 'Receiver Pivot'}
          {pivotData.pivot_type === 'intent_action' && 'Intent Action Pivot'}
        </PivotTitle>
        <PivotSubtitle>
          {pivotData.pivot_type === 'correlated_samples'
            ? `APKs sharing at least one artifact with this sample, sorted by overlap`
            : pivotData.pivot_type === 'service'
            ? <>APKs declaring this Android service: <PivotValue>{pivotData.pivot_value}</PivotValue></>
            : pivotData.pivot_type === 'receiver'
            ? <>APKs registering this broadcast receiver: <PivotValue>{pivotData.pivot_value}</PivotValue></>
            : pivotData.pivot_type === 'intent_action'
            ? <>APKs registering this intent action: <PivotValue>{pivotData.pivot_value}</PivotValue></>
            : <>Pivoting on: <PivotValue>{pivotData.pivot_value}</PivotValue></>
          }
        </PivotSubtitle>
      </PivotHeader>

      <StatsGrid>
        <StatCard>
          <h4>Total Matches</h4>
          <p>{pivotData.total_matches}</p>
        </StatCard>
        <StatCard>
          <h4>Returned Results</h4>
          <p>{pivotData.returned_count}</p>
        </StatCard>
        {relatedCount.total_unique_families && (
          <StatCard>
            <h4>Unique Families</h4>
            <p>{relatedCount.total_unique_families}</p>
          </StatCard>
        )}
        {relatedCount.total_unique_packages && (
          <StatCard>
            <h4>Unique Packages</h4>
            <p>{relatedCount.total_unique_packages}</p>
          </StatCard>
        )}
      </StatsGrid>

      {renderRelatedCount()}

      {results.length > 0 ? (
        <>
          <ResultsTable>
            <thead>
              <tr>
                <TableHeader>Icon</TableHeader>
                <TableHeader>SHA256</TableHeader>
                <TableHeader>Package Name</TableHeader>
                <TableHeader>App Name</TableHeader>
                <TableHeader>Malware Family</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Analysis Date</TableHeader>
                {isCorrelated && <TableHeader>Shared Artifacts</TableHeader>}
              </tr>
            </thead>
            <tbody>
              {results.map((result, index) => (
                <tr key={index}>
                  <TableCell>
                    {result.icon ? (
                      <AppIcon
                        src={`data:image/png;base64,${result.icon}`}
                        alt="App Icon"
                        onError={(e) => {
                          e.target.src =
                            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
                        }}
                      />
                    ) : (
                      <AppIcon
                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                        alt="No Icon"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <HashLink
                      onClick={() => navigate(`/analysis/${result.sha256}`)}
                      title={result.sha256}
                    >
                      {result.sha256.substring(0, 8)}...
                    </HashLink>
                  </TableCell>
                  <TableCell>{result.package_name}</TableCell>
                  <TableCell>{result.app_name}</TableCell>
                  <TableCell>
                    <MalwareBadge type={result.malware_family === 'Unknown' ? 'unknown' : 'malware'}>
                      {result.malware_family || 'Unknown'}
                    </MalwareBadge>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={result.analysis_status}>
                      {result.analysis_status}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    {result.timestamp
                      ? new Date(result.timestamp).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  {isCorrelated && (
                    <TableCell>
                      <MalwareBadge type="unknown">
                        {result.shared_artifact_count} file{result.shared_artifact_count !== 1 ? 's' : ''}
                      </MalwareBadge>
                    </TableCell>
                  )}
                </tr>
              ))}
            </tbody>
          </ResultsTable>
        </>
      ) : (
        <EmptyState>
          <div style={{ fontSize: '3rem' }}></div>
          <h3>No Results Found</h3>
          <p>
            No APKs found matching the pivot criteria
            {pivotData.pivot_type === 'icon_hash' && ' with this icon hash.'}
            {pivotData.pivot_type === 'certificate_sha256' && ' with this certificate.'}
            {pivotData.pivot_type === 'permission' && ' requesting this permission.'}
            {pivotData.pivot_type === 'malware_family' && ' in this malware family.'}
            {pivotData.pivot_type === 'artifact_hash' && ' containing this artifact.'}
            {pivotData.pivot_type === 'correlated_samples' && ' sharing artifacts with this sample.'}
            {pivotData.pivot_type === 'service' && ' declaring this service.'}
            {pivotData.pivot_type === 'receiver' && ' registering this broadcast receiver.'}
            {pivotData.pivot_type === 'intent_action' && ' registering this intent action.'}
          </p>
        </EmptyState>
      )}
    </PivotContainer>
  );
};

export default PivotResults;